/**
 * Operator calls of the load/queue and model/provider panels (OBS-006,
 * AGP-015). They use the operator session cookie, like the inspector.
 */

export type Degraded = "none" | "unavailable";

export interface OverviewData {
  window_hours: number;
  jobs_by_state: Record<string, number>;
  duration_seconds: { p50: number | null; p95: number | null; samples: number };
  failure_rate: number | null;
  hold: { state: string; waiting_jobs: number };
}

export interface QueuesData {
  degraded: Degraded;
  queues: { queue: string; ready: number; consumers: number }[];
  outbox_lag_seconds: number | null;
  jobs_waiting: { accepted: number; queued: number; waiting: number };
}

export interface LoadData {
  degraded: Degraded;
  window_minutes: number;
  job_classes: {
    job_class: string;
    busy: number;
    wait_p95_seconds: number | null;
  }[];
  vllm: {
    running: number | null;
    waiting: number | null;
    kv_cache_usage: number | null;
  };
}

interface ModelStat {
  provider: string;
  model: string;
  calls: number;
  errors: number;
  latency_ms_p50: number | null;
  latency_ms_p95: number | null;
  input_tokens: number;
  output_tokens: number;
  spend_micros: number;
  /** `internal` or `external` (ADR 0232). */
  exposure?: string | null;
  /** Rolling rating 0..1 from the usage ledger; null while unrated. */
  rating?: number | null;
  /** `closed`, `open` or `half_open`. */
  breaker?: string | null;
}

export interface ModelsData {
  window_hours: number;
  models: ModelStat[];
}

export interface ErrorsData {
  window_hours: number;
  top_outcomes: {
    provider: string;
    outcome: string;
    error_class: string | null;
    count: number;
  }[];
}

interface ProviderHealth {
  rating: number | null;
  success_rate: number | null;
  latency_ms: number | null;
  samples: number;
  breaker: string;
}

interface RegistryProvider {
  id: string;
  family: string;
  kind: string;
  locality: string;
  exposure?: string;
  health?: ProviderHealth;
  file_enabled: boolean;
  effective_enabled: boolean;
  spend_today_micros: number | null;
}

interface RegistryOverride {
  scope: string;
  provider_id: string;
  target_id: string;
  setting: string;
  value: boolean;
  reason_code: string;
}

export interface RegistryStatus {
  registry_version: string;
  remote_ability_brief_enabled?: boolean;
  /** False when the server will refuse remote-user-content changes. */
  remote_user_content_mutable?: boolean;
  providers: RegistryProvider[];
  overrides: RegistryOverride[];
}

export const REASON_CODES = [
  "incident",
  "quality_regression",
  "cost",
  "maintenance",
  "rollout",
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

/** The operator session is missing or lacks the permission. */
export class ObservabilityDenied extends Error {}

/** HTTP 401: the operator session is gone (8 h TTL, no refresh). */
export class ObservabilitySessionExpired extends ObservabilityDenied {}

/** The server refused the command with a stable code (for example 409). */
export class ObservabilityRefused extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code);
  }
}

export interface ObservabilityApi {
  overview: (windowHours: number) => Promise<OverviewData>;
  queues: () => Promise<QueuesData>;
  load: () => Promise<LoadData>;
  models: (windowHours: number) => Promise<ModelsData>;
  errors: (windowHours: number) => Promise<ErrorsData>;
  registry: () => Promise<RegistryStatus>;
  setProvider: (
    providerId: string,
    enabled: boolean,
    reason: ReasonCode,
  ) => Promise<void>;
  setRemoteUserContent: (enabled: boolean, reason: ReasonCode) => Promise<void>;
  setRemoteAbilityBrief: (
    enabled: boolean,
    reason: ReasonCode,
  ) => Promise<void>;
}

export function createObservabilityApi(
  fetcher: typeof fetch = fetch,
): ObservabilityApi {
  const call = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetcher(`/operator${path}`, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (response.status === 401) {
      throw new ObservabilitySessionExpired("401");
    }
    if (response.status === 403) {
      throw new ObservabilityDenied("403");
    }
    if (!response.ok) {
      let code = `observability_http_${String(response.status)}`;
      try {
        const body = (await response.json()) as { detail?: unknown };
        if (typeof body.detail === "string") code = body.detail;
      } catch {
        // keep the generic code
      }
      throw new ObservabilityRefused(code, response.status);
    }
    return (await response.json()) as T;
  };
  const post = async (path: string, body: unknown): Promise<void> => {
    await call(path, { method: "POST", body: JSON.stringify(body) });
  };
  return {
    overview: (h) => call(`/observability/overview?window_hours=${String(h)}`),
    queues: () => call("/observability/queues"),
    load: () => call("/observability/load"),
    models: (h) => call(`/observability/models?window_hours=${String(h)}`),
    errors: (h) => call(`/observability/errors?window_hours=${String(h)}`),
    registry: () => call("/model-registry"),
    setProvider: (providerId, enabled, reason) =>
      post(`/model-registry/${enabled ? "enable" : "disable"}`, {
        scope: "provider",
        provider_id: providerId,
        target_id: "",
        reason_code: reason,
      }),
    setRemoteUserContent: (enabled, reason) =>
      post("/model-registry/remote-user-content", {
        enabled,
        reason_code: reason,
      }),
    setRemoteAbilityBrief: (enabled, reason) =>
      post("/model-registry/ability-brief", {
        enabled,
        reason_code: reason,
      }),
  };
}
